import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';
import { Globe, ArrowLeft, Shield } from 'lucide-react';
import { Button, LanguageModal } from '@/components/ui';
import { SignInButton, UserButton, useAuth } from '@clerk/clerk-react';
import React from 'react';
import { PageSeo } from '@/components/seo/PageSeo';

function renderInlineBold(text: string): React.ReactNode[] {
  const parts: React.ReactNode[] = [];
  let remaining = text;
  let key = 0;
  while (remaining.length > 0) {
    const start = remaining.indexOf('**');
    if (start === -1) {
      parts.push(remaining);
      break;
    }
    const end = remaining.indexOf('**', start + 2);
    if (end === -1) {
      parts.push(remaining);
      break;
    }
    if (start > 0) parts.push(remaining.slice(0, start));
    parts.push(<strong key={key++}>{remaining.slice(start + 2, end)}</strong>);
    remaining = remaining.slice(end + 2);
  }
  return parts;
}

const content = {
  hr: {
    title: 'Politika privatnosti',
    lastUpdated: 'Zadnje ažurirano: 23. listopada 2026.',
    intro: 'BookiApp ("mi", "nas" ili "naš") cijeni vašu privatnost. Ova politika privatnosti objašnjava kako prikupljamo, koristimo i štitimo vaše osobne podatke.',
    sections: [
      {
        title: '1. Voditelj obrade podataka',
        content: `BookiApp
Email: admin@bookiapp.com
Web: https://bookiapp.com

Za sva pitanja vezana uz zaštitu osobnih podataka, možete nas kontaktirati na gore navedenu email adresu.`,
      },
      {
        title: '2. Podaci koje prikupljamo',
        content: `Prikupljamo sljedeće vrste podataka:

**Podaci o računu:**
- Email adresa
- Ime i prezime
- Broj telefona (opcionalno)

**Podaci o objektima:**
- Nazivi i opisi nekretnina
- Adrese i lokacije
- Fotografije smještaja

**Podaci o rezervacijama:**
- Imena gostiju
- Datumi boravka
- Kontakt podaci gostiju

**Tehnički podaci:**
- IP adresa
- Vrsta preglednika
- Podaci o uređaju

**Aktivnost u Guest Portalu:**
- Vrijeme prve i zadnje posjete portalu
- Status pretplate na push obavijesti
- Status pročitanosti poruka

Ovi podaci su vidljivi samo domaćinu vaše rezervacije i koriste se isključivo da domaćin zna može li vas učinkovito kontaktirati putem aplikacije ili treba koristiti drugi kanal komunikacije (telefon, email).`,
      },
      {
        title: '3. Pravna osnova obrade',
        content: `Osobne podatke obrađujemo temeljem:

- **Ugovorne obveze**: Obrada nužna za pružanje usluge
- **Legitimnog interesa**: Poboljšanje usluge i sigurnost
- **Privole**: Za marketing komunikaciju (možete je povući u bilo kojem trenutku)`,
      },
      {
        title: '4. Kako koristimo vaše podatke',
        content: `Vaše podatke koristimo za:

- Pružanje i održavanje naše usluge
- Omogućavanje komunikacije između domaćina i gostiju
- Slanje obavijesti vezanih uz uslugu
- **Praćenje aktivnosti u Guest Portalu** kako bi domaćin znao je li gost otvorio portal i može li ga učinkovito kontaktirati putem aplikacije (legitimni interes, čl. 6(1)(f) GDPR-a)
- Poboljšanje korisničkog iskustva
- Zaštitu od prijevara i zlouporabe`,
      },
      {
        title: '5. Dijeljenje podataka',
        content: `Vaše podatke dijelimo samo s:

- **Pružateljima usluga**: Clerk (autentifikacija), Supabase (baza podataka), Stripe (plaćanja), Sentry (praćenje grešaka), Google Gemini (AI prijevodi poruka i sadržaja te AI asistenti), Google Analytics (statistika javnih stranica, samo uz pristanak)
- **Gostima**: Samo informacije potrebne za boravak
- **Zakonskim tijelima**: Kada je to zakonski obvezno

Nikada ne prodajemo vaše osobne podatke trećim stranama.`,
      },
      {
        title: '6. Vaša prava (GDPR)',
        content: `Kao stanovnik EU, imate pravo na:

- **Pristup**: Zatražite kopiju svojih podataka
- **Ispravak**: Ispravite netočne podatke
- **Brisanje**: Zatražite brisanje podataka ("pravo na zaborav")
- **Ograničenje obrade**: Ograničite kako koristimo vaše podatke
- **Prenosivost**: Preuzmite podatke u strojno čitljivom formatu
- **Prigovor**: Usprotivite se obradi u određenim situacijama

Za ostvarivanje prava, kontaktirajte nas na admin@bookiapp.com.`,
      },
      {
        title: '7. Sigurnost podataka',
        content: `Primjenjujemo tehničke i organizacijske mjere za zaštitu podataka:

- Enkripcija podataka u prijenosu (HTTPS/TLS)
- Sigurno pohranjivanje lozinki
- Redovite sigurnosne provjere
- Ograničen pristup podacima`,
      },
      {
        title: '8. Smart Inbox i podaci iz e-pošte (Gmail integracija)',
        content: `Smart Inbox je opcionalna funkcija: povežete svoj Gmail i na upite gostiju odgovarate izravno iz BookiAppa. Ako ga povežete, vrijede sljedeća pravila:

**Pristupne dozvole (OAuth scopes):**
- **gmail.readonly** — čitanje primljenih e-mailova kako bismo prepoznali upite gostiju
- **gmail.send** — slanje odgovora gostima u vaše ime, samo kad vi kliknete „Pošalji”
- **userinfo.email i userinfo.profile** — povezivanje Gmail računa s vašim BookiApp profilom
- Ne tražimo dozvolu za mijenjanje ili brisanje poruka — BookiApp u vašem Gmailu ništa ne označava, ne premješta i ne briše

**Što spremamo:**
- Primljene e-mailove iz vašeg sandučića (pošiljatelja, adresu za odgovor, naslov, tekst i datum) — osim pošte iz Gmailovih kartica Promocije, Društvene mreže i Forumi te newslettera i druge masovne pošte koja je kao takva označena; njih ne spremamo
- Vaše poslane odgovore, samo u razgovorima koje već spremamo (da vidite cijeli razgovor)
- Poštu stariju od 12 mjeseci ne čuvamo
- Odgovore koje pošaljete iz BookiAppa te AI analizu upita (datumi, broj gostiju, dostupnost, prijedlog odgovora)
- Pristupne tokene za Gmail — čuvaju se samo na poslužitelju i nikad ne dolaze u preglednik

**Kako ih obrađujemo:**
- AI (Google Gemini) obrađuje sadržaj e-maila samo na zahtjev — kad otvorite upit ili kliknete „Analiziraj” — kako bi prepoznao upit, datume i broj gostiju te predložio odgovor
- Kad zatražite prijevod, tekst vašeg odgovora šalje se Geminiju da ga prevede na jezik gosta
- Kad kliknete „Nauči iz Gmaila”, tekst vaših zadnjih 20 poslanih e-mailova jednokratno se šalje Geminiju da predloži pravila odgovora; ti se e-mailovi ne spremaju
- Gostu se ništa ne šalje automatski — odgovor odlazi tek kad vi kliknete „Pošalji”

**Što NE radimo s vašim Gmail podacima:**
- **Ne prodajemo vaše Gmail podatke** trećim stranama
- **Ne koristimo vaše Gmail podatke za oglašavanje**
- **Ne koristimo vaše Gmail podatke za treniranje AI modela** (Gemini API ne trenira na našim upitima)
- **Ne dijelimo vaše Gmail podatke** s drugim korisnicima BookiAppa
- Ljudi ne čitaju vaše e-mailove osim u svrhu sigurnosti, otklanjanja kvarova ili kada je to zakonski obvezno

**Google API Services User Data Policy:**
BookiApp koristi i prenosi informacije primljene iz Google API-ja u skladu s Google API Services User Data Policy (developers.google.com/terms/api-services-user-data-policy), uključujući zahtjeve za **Limited Use**.

**Brisanje:**
- Kad odspojite sandučić u postavkama Smart Inboxa, odmah brišemo sve e-mailove tog sandučića spremljene u BookiAppu (primljene e-mailove, vaše odgovore i AI analize) i opozivamo pristup kod Googlea
- Kad zatražite brisanje BookiApp računa, odmah odspajamo sandučić, brišemo sve spremljene e-mailove i opozivamo pristup kod Googlea; postavke Smart Inboxa brišu se zajedno s računom, koji se trajno briše 30 dana nakon zahtjeva (do tada ga možete vratiti)
- Spremljeni e-mailovi stariji od 12 mjeseci automatski se brišu
- E-mailovi spremljeni prije 23. listopada 2026. koji više nisu vezani ni uz jedan povezani sandučić (ostali nakon ranijih odspajanja) ne brišu se odspajanjem, nego brisanjem vašeg računa, a najkasnije 12 mjeseci nakon primitka
- Pristup možete opozvati i sami na myaccount.google.com/permissions

**Outlook:**
Povezivanje Microsoft Outlook računa je u pripremi. Kad bude dostupno, vrijedit će ista pravila, a ovu ćemo politiku ažurirati.`,
      },
      {
        title: '9. Razdoblje čuvanja',
        content: `Podatke čuvamo dok:

- Imate aktivan račun kod nas
- Je potrebno za pružanje usluge
- Postoji zakonska obveza čuvanja

Nakon brisanja računa, podaci se brišu unutar 30 dana, osim ako zakon zahtijeva duže čuvanje.`,
      },
      {
        title: '10. Kolačići',
        content: `Koristimo kolačiće za funkcioniranje usluge. Za više informacija pogledajte našu Politiku kolačića.`,
      },
      {
        title: '11. Promjene politike',
        content: `Možemo ažurirati ovu politiku privatnosti. O značajnim promjenama ćemo vas obavijestiti putem emaila ili obavijesti u aplikaciji.`,
      },
      {
        title: '12. Kontakt',
        content: `Za pitanja o privatnosti:

Email: admin@bookiapp.com

Imate pravo podnijeti pritužbu nadzornom tijelu za zaštitu podataka u vašoj zemlji.`,
      },
    ],
  },
  en: {
    title: 'Privacy Policy',
    lastUpdated: 'Last updated: October 23, 2026',
    intro: 'BookiApp ("we", "us" or "our") values your privacy. This privacy policy explains how we collect, use and protect your personal data.',
    sections: [
      {
        title: '1. Data Controller',
        content: `BookiApp
Email: admin@bookiapp.com
Web: https://bookiapp.com

For all questions regarding personal data protection, you can contact us at the email address above.`,
      },
      {
        title: '2. Data We Collect',
        content: `We collect the following types of data:

**Account Data:**
- Email address
- First and last name
- Phone number (optional)

**Property Data:**
- Property names and descriptions
- Addresses and locations
- Accommodation photos

**Booking Data:**
- Guest names
- Stay dates
- Guest contact information

**Technical Data:**
- IP address
- Browser type
- Device information

**Guest Portal Activity:**
- Time of first and last visit to the portal
- Push notification subscription status
- Message read status

This data is visible only to the host of your booking and is used solely so the host knows whether they can effectively reach you through the app or should use another communication channel (phone, email).`,
      },
      {
        title: '3. Legal Basis for Processing',
        content: `We process personal data based on:

- **Contractual obligation**: Processing necessary for service provision
- **Legitimate interest**: Service improvement and security
- **Consent**: For marketing communications (you can withdraw at any time)`,
      },
      {
        title: '4. How We Use Your Data',
        content: `We use your data to:

- Provide and maintain our service
- Enable communication between hosts and guests
- Send service-related notifications
- **Track Guest Portal activity** so the host knows whether the guest has opened the portal and can effectively be reached through the app (legitimate interest, GDPR Art. 6(1)(f))
- Improve user experience
- Protect against fraud and abuse`,
      },
      {
        title: '5. Data Sharing',
        content: `We only share your data with:

- **Service providers**: Clerk (authentication), Supabase (database), Stripe (payments), Sentry (error tracking), Google Gemini (AI translation of messages and content, AI assistants), Google Analytics (public-page statistics, only with consent)
- **Guests**: Only information necessary for their stay
- **Legal authorities**: When legally required

We never sell your personal data to third parties.`,
      },
      {
        title: '6. Your Rights (GDPR)',
        content: `As an EU resident, you have the right to:

- **Access**: Request a copy of your data
- **Rectification**: Correct inaccurate data
- **Erasure**: Request data deletion ("right to be forgotten")
- **Restriction**: Limit how we use your data
- **Portability**: Download data in machine-readable format
- **Object**: Object to processing in certain situations

To exercise your rights, contact us at admin@bookiapp.com.`,
      },
      {
        title: '7. Data Security',
        content: `We implement technical and organizational measures to protect data:

- Data encryption in transit (HTTPS/TLS)
- Secure password storage
- Regular security audits
- Limited data access`,
      },
      {
        title: '8. Smart Inbox and E-mail Data (Gmail Integration)',
        content: `Smart Inbox is an optional feature: you connect your Gmail and reply to guest inquiries directly from BookiApp. If you connect it, the following rules apply:

**Access permissions (OAuth scopes):**
- **gmail.readonly** — read your incoming emails so we can recognise guest inquiries
- **gmail.send** — send replies to guests on your behalf, only when you click "Send"
- **userinfo.email and userinfo.profile** — link your Gmail account to your BookiApp profile
- We do not ask for permission to change or delete messages — BookiApp never marks, moves or deletes anything in your Gmail

**What we store:**
- Incoming emails from your mailbox (sender, reply-to address, subject, text and date) — except mail in Gmail's Promotions, Social and Forums tabs and newsletters or other bulk mail marked as such; we do not store those
- Your sent replies, only in conversations we already store (so you see the whole conversation)
- We do not keep mail older than 12 months
- Replies you send from BookiApp and the AI analysis of an inquiry (dates, number of guests, availability, suggested reply)
- Gmail access tokens — kept on the server only, never sent to the browser

**How we process them:**
- AI (Google Gemini) processes an email's content only on demand — when you open an inquiry or click "Analyze" — to recognise the inquiry, the dates and number of guests, and to suggest a reply
- When you ask for a translation, the text of your reply is sent to Gemini to translate it into the guest's language
- When you click "Learn from Gmail", the text of your last 20 sent emails is sent to Gemini once to suggest reply rules; those emails are not stored
- Nothing is sent to a guest automatically — a reply goes out only when you click "Send"

**What we do NOT do with your Gmail data:**
- **We do not sell your Gmail data** to third parties
- **We do not use your Gmail data for advertising**
- **We do not use your Gmail data to train AI models** (Gemini API does not train on our requests)
- **We do not share your Gmail data** with other BookiApp users
- Humans do not read your emails except for security, troubleshooting, or when legally required

**Google API Services User Data Policy:**
BookiApp's use and transfer of information received from Google APIs adheres to the Google API Services User Data Policy (developers.google.com/terms/api-services-user-data-policy), including the **Limited Use** requirements.

**Deletion:**
- When you disconnect a mailbox in Smart Inbox settings, we immediately delete every email of that mailbox stored in BookiApp (received emails, your replies and AI analyses) and revoke access at Google
- When you ask us to delete your BookiApp account, we disconnect the mailbox right away, delete every stored email and revoke access at Google; the Smart Inbox settings are deleted with the account, which is permanently deleted 30 days after the request (until then you can restore it)
- Stored emails older than 12 months are deleted automatically
- Emails stored before October 23, 2026 that are no longer linked to any connected mailbox (left over from earlier disconnections) are not deleted by disconnecting; they are deleted with your account, and at the latest 12 months after they were received
- You can also revoke access yourself at myaccount.google.com/permissions

**Outlook:**
Connecting a Microsoft Outlook account is in preparation. When it becomes available the same rules will apply, and we will update this policy.`,
      },
      {
        title: '9. Retention Period',
        content: `We retain data while:

- You have an active account with us
- Necessary for service provision
- Legal retention obligation exists

After account deletion, data is deleted within 30 days, unless law requires longer retention.`,
      },
      {
        title: '10. Cookies',
        content: `We use cookies for service functionality. For more information, see our Cookie Policy.`,
      },
      {
        title: '11. Policy Changes',
        content: `We may update this privacy policy. We will notify you of significant changes via email or in-app notification.`,
      },
      {
        title: '12. Contact',
        content: `For privacy questions:

Email: admin@bookiapp.com

You have the right to file a complaint with the data protection supervisory authority in your country.`,
      },
    ],
  },
};

export function PrivacyPage() {
  const { i18n } = useTranslation();
  const lang = (i18n.language === 'hr' ? 'hr' : 'en') as 'hr' | 'en';
  const t = content[lang];

  const [langOpen, setLangOpen] = useState(false);
  const { isSignedIn } = useAuth();

  return (
    <div className="min-h-screen bg-[hsl(var(--background))]">
      <PageSeo path="/privacy" title={`${t.title} | BookiApp`} description={t.intro} />
      {/* Header */}
      <header className="border-b">
        <div className="container mx-auto flex items-center justify-between p-4">
          <Link to="/" className="flex items-center gap-2">
            <img src="/favicon-original.png" alt="BookiApp" className="h-8 w-auto" />
          </Link>
          <div className="flex items-center gap-2">
            <Button variant="ghost" size="sm" onClick={() => setLangOpen(true)} aria-haspopup="dialog" aria-expanded={langOpen}>
              <Globe className="h-4 w-4 mr-2" />
              {i18n.language.toUpperCase()}
            </Button>
            {isSignedIn ? (
              <>
                <Link to="/dashboard">
                  <Button size="sm" className="bg-gradient-to-r from-cyan-500 to-blue-600 hover:from-cyan-600 hover:to-blue-700 text-white border-0">
                    Dashboard
                  </Button>
                </Link>
                <UserButton
                  afterSignOutUrl="/"
                  appearance={{ elements: { avatarBox: 'h-9 w-9 rounded-xl ring-2 ring-transparent hover:ring-cyan-200 transition-all duration-200' } }}
                />
              </>
            ) : (
              <SignInButton mode="modal" forceRedirectUrl="/dashboard" signUpForceRedirectUrl="/dashboard">
                <Button variant="ghost" size="sm" className="hover:bg-cyan-500/10">
                  {lang === 'hr' ? 'Prijava' : 'Sign In'}
                </Button>
              </SignInButton>
            )}
          </div>
        </div>
      </header>

      {/* Content */}
      <main className="container mx-auto px-4 py-12 max-w-3xl">
        <Link
          to="/"
          className="inline-flex items-center gap-2 text-[hsl(var(--muted-foreground))] hover:text-[hsl(var(--foreground))] mb-8"
        >
          <ArrowLeft className="h-4 w-4" />
          {lang === 'hr' ? 'Natrag' : 'Back'}
        </Link>

        <div className="flex items-center gap-3 mb-6">
          <div className="w-12 h-12 rounded-xl bg-gradient-to-br from-cyan-500/10 to-blue-500/10 flex items-center justify-center">
            <Shield className="h-6 w-6 text-cyan-500" />
          </div>
          <div>
            <h1 className="text-3xl font-bold">{t.title}</h1>
            <p className="text-sm text-[hsl(var(--muted-foreground))]">{t.lastUpdated}</p>
          </div>
        </div>

        <p className="text-[hsl(var(--muted-foreground))] mb-8">{t.intro}</p>

        <div className="space-y-8">
          {t.sections.map((section, i) => (
            <section key={i}>
              <h2 className="text-xl font-semibold mb-3">{section.title}</h2>
              <div className="prose prose-sm dark:prose-invert max-w-none">
                {section.content.split('\n').map((line, j) => {
                  if (!line.trim()) return null;
                  if (line.startsWith('- ')) {
                    return <p key={j} className="ml-4 text-[hsl(var(--muted-foreground))]">- {renderInlineBold(line.slice(2))}</p>;
                  }
                  if (line.includes('**')) {
                    return <p key={j} className="text-[hsl(var(--muted-foreground))] mt-2">{renderInlineBold(line)}</p>;
                  }
                  return <p key={j} className="text-[hsl(var(--muted-foreground))]">{line}</p>;
                })}
              </div>
            </section>
          ))}
        </div>
      </main>

      {/* Footer */}
      <footer className="border-t py-8 mt-12">
        <div className="container mx-auto px-4 text-center text-sm text-[hsl(var(--muted-foreground))]">
          <p>&copy; 2026 BookiApp. {lang === 'hr' ? 'Sva prava pridržana.' : 'All rights reserved.'}</p>
        </div>
      </footer>

      <LanguageModal open={langOpen} onClose={() => setLangOpen(false)} scope="marketing" />
    </div>
  );
}

export default PrivacyPage;
